import { useEffect, useState } from 'react';
import { howItWorksApi } from './api';
import { slugify } from './slugify';

// How Does It Work? shows up to this many topics per section/subsection "Top Topics"
// row; a scope with more than this gets a "More Topics" link. Shared by the landing
// page's per-category loop and the section/subsection page so both stay in agreement.
export const TOP_TOPICS_N = 6;

// Fetches the full How Does It Work? topic catalog once. Every hierarchy page (landing,
// section, subsection, "More Topics") derives its view from this same list by filtering/
// grouping client-side — there is no server-side concept of a section/subsection, only
// flat `category`/`subcategory` strings on each topic (see groupTopicsByCategory below).
export const useHowItWorksTopics = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    howItWorksApi
      .getTopics()
      .then((data) => {
        if (!cancelled) setTopics(data);
      })
      .catch((err) => {
        console.error('Error fetching how-it-works topics:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { topics, loading };
};

// Groups topics into [category, [[subcategory, topics[]], ...]] entries, preserving
// first-appearance order at both levels (already sorted by the API). Topics with no
// category fall into a trailing "Other" bucket; topics with no subcategory share a
// single null-keyed subgroup that renders flat (no subheader).
export const groupTopicsByCategory = (topics) => {
  const catGroups = new Map();
  topics.forEach((topic) => {
    const category = topic.category || 'Other';
    if (!catGroups.has(category)) catGroups.set(category, new Map());
    const subGroups = catGroups.get(category);
    const subcategory = topic.subcategory || null;
    if (!subGroups.has(subcategory)) subGroups.set(subcategory, []);
    subGroups.get(subcategory).push(topic);
  });
  return Array.from(catGroups.entries()).map(([category, subGroups]) => [
    category,
    Array.from(subGroups.entries()),
  ]);
};

// The display name for a category, e.g. "EE Architecture" -> slug "ee-architecture".
export const categorySlugOf = (topic) => slugify(topic.category || 'Other');
export const subcategorySlugOf = (topic) => (topic.subcategory ? slugify(topic.subcategory) : null);

// Resolves a category slug (and, optionally, a subcategory slug within it) back to its
// real display name, by finding the first matching topic — there is no separate lookup
// table since category/subcategory are just strings carried on each topic.
export const resolveScopeName = (topics, categorySlug, subcategorySlug) => {
  const inCategory = topics.find((t) => categorySlugOf(t) === categorySlug);
  if (!inCategory) return { categoryName: null, subcategoryName: null };
  if (!subcategorySlug) return { categoryName: inCategory.category || 'Other', subcategoryName: null };
  const inSubcategory = topics.find(
    (t) => categorySlugOf(t) === categorySlug && subcategorySlugOf(t) === subcategorySlug
  );
  return {
    categoryName: inCategory.category || 'Other',
    subcategoryName: inSubcategory ? inSubcategory.subcategory : null,
  };
};

// All topics belonging to one category, optionally narrowed to one subcategory within it.
export const topicsInScope = (topics, categorySlug, subcategorySlug) =>
  topics.filter((t) => {
    if (categorySlugOf(t) !== categorySlug) return false;
    if (subcategorySlug) return subcategorySlugOf(t) === subcategorySlug;
    return true;
  });

// [subcategory name, topics[]] groups within one category's already-scoped topic list,
// preserving first-appearance order. Topics with no subcategory share a null-keyed group
// (rendered without a subheader by callers) — there are never empty groups here since a
// group only exists if at least one topic produced it.
export const groupBySubcategory = (topics) => {
  const groups = new Map();
  topics.forEach((topic) => {
    const key = topic.subcategory || null;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(topic);
  });
  return Array.from(groups.entries());
};

// Shared shape consumed by <TrendingSection>/<TrendingCard>.
export const topicToTrendingItem = (topic) => ({
  id: `/how-it-works/${topic.topic_id}`,
  to: `/how-it-works/${topic.topic_id}`,
  title: topic.title,
  description: topic.short_description,
  badge: topic.subcategory || topic.category,
  updatedAt: topic.updated_at,
  contentType: 'how_it_works_topic',
  contentId: topic.topic_id,
});
